/**
 * SuccessStep Component
 * Success message UI
 */

import React from 'react';
import {
  CheckCircle,
  Calendar,
  Clock,
  MessageSquare,
  Video,
  MapPin,
  Users,
  Download,
} from 'lucide-react';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Button } from '../../button.js';
import { BookingData, BookingSuccessData } from '../utils/bookingConstants.js';
import { formatPersonName } from '../utils/bookingFormatters.js';
import { formatTimeZoneDisplay } from '../utils/bookingTimezones.js';
import { buildPublicBookingIcsContent } from '../../../lib/handlers/public-booking-calendar-invite.js';

interface SuccessStepProps {
  bookingData: BookingData;
  meetingDetails: BookingSuccessData | null;
  onBookAnother: () => void;
}

export function SuccessStep({ bookingData, meetingDetails, onBookAnother }: SuccessStepProps) {
  // Confirm in the BOOKER's zone. The API echoes the ORGANIZER's zone, so
  // rendering that here would contradict the times the booker just picked.
  const timeZone = bookingData.timeZone;
  const confirmedStart = meetingDetails?.start ?? bookingData.selectedSlot?.start;
  const handleDownloadIcs = () => {
    const startUtcIso = meetingDetails?.start ?? bookingData.selectedSlot?.start;
    const endUtcIso = meetingDetails?.end ?? bookingData.selectedSlot?.end;

    if (!startUtcIso || !endUtcIso) {
      return;
    }

    const fallbackTitle = bookingData.bookeeEmail
      ? `Meeting with ${formatPersonName(bookingData.bookeeEmail)}`
      : "Meeting";
    const title = meetingDetails?.title?.trim() || fallbackTitle;
    const description = meetingDetails?.description?.trim() || bookingData.description?.trim() || "";
    const location = meetingDetails?.location?.trim() || bookingData.location?.trim() || "";
    const meetLink = meetingDetails?.hangoutLink;
    const organizerEmail = bookingData.bookeeEmail || bookingData.email || "no-reply@westtn.consulting";
    const attendeeEmails = [
      bookingData.email,
      ...(meetingDetails?.attendees?.map((attendee) => attendee.email) ?? []),
    ].filter(Boolean);
    const uidBase = meetingDetails?.eventId || `${startUtcIso}-${bookingData.email || bookingData.bookeeEmail || "public-booking"}`;
    const uid = uidBase.replace(/[^A-Za-z0-9@._-]/g, "-");

    const icsContent = buildPublicBookingIcsContent({
      uid,
      title,
      startUtcIso,
      endUtcIso,
      description,
      location,
      organizerEmail,
      attendeeEmails,
      meetLink,
    });

    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "invite.ics";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const canDownloadIcs = Boolean(
    (meetingDetails?.start && meetingDetails?.end) || bookingData.selectedSlot
  );

  return (
    <div className="space-y-6">
      <div className="text-center">
        <CheckCircle className="w-20 h-20 mx-auto mb-6 text-green-600" />
        <h3 className="text-2xl font-bold text-card-foreground mb-4">Booking Confirmed!</h3>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          Your meeting has been successfully scheduled. You'll receive a calendar invitation shortly.
          You can also add it to your calendar now.
        </p>
      </div>

      {/* Meeting Details from API Response */}
      {meetingDetails && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 space-y-4">
          <h4 className="font-semibold text-lg text-card-foreground mb-4">Meeting Details</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <Calendar className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-card-foreground">Date & Time</p>
                  {meetingDetails.start && meetingDetails.end ? (
                    <p className="text-sm text-gray-600">
                      {formatInTimeZone(
                        parseISO(meetingDetails.start),
                        timeZone,
                        'EEEE, MMMM d, yyyy'
                      )}
                      <br />
                      {formatInTimeZone(
                        parseISO(meetingDetails.start),
                        timeZone,
                        'h:mm a'
                      )}{' '}
                      -{' '}
                      {formatInTimeZone(
                        parseISO(meetingDetails.end),
                        timeZone,
                        'h:mm a'
                      )}
                      <span className="text-gray-500">
                        {' '}
                        ({formatTimeZoneDisplay(
                          timeZone,
                          confirmedStart ? parseISO(confirmedStart) : undefined
                        )})
                      </span>
                    </p>
                  ) : bookingData.selectedSlot ? (
                    <p className="text-sm text-gray-600">
                      {formatInTimeZone(
                        parseISO(bookingData.selectedSlot.start),
                        timeZone,
                        'EEEE, MMMM d, yyyy'
                      )}
                      <br />
                      {formatInTimeZone(
                        parseISO(bookingData.selectedSlot.start),
                        timeZone,
                        'h:mm a'
                      )}{' '}
                      -{' '}
                      {formatInTimeZone(
                        parseISO(bookingData.selectedSlot.end),
                        timeZone,
                        'h:mm a'
                      )}
                    </p>
                  ) : null}
                </div>
              </div>

              {meetingDetails.title && (
                <div className="flex items-start space-x-3">
                  <MessageSquare className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-card-foreground">Title</p>
                    <p className="text-sm text-gray-600">{meetingDetails.title}</p>
                  </div>
                </div>
              )}

              {meetingDetails.description && (
                <div className="flex items-start space-x-3">
                  <MessageSquare className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-card-foreground">Description</p>
                    <p className="text-sm text-gray-600">{meetingDetails.description}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3">
              {meetingDetails.isOnline && meetingDetails.hangoutLink ? (
                <div className="flex items-start space-x-3">
                  <Video className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-card-foreground">Google Meet Link</p>
                    <a
                      href={meetingDetails.hangoutLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 underline break-all"
                    >
                      {meetingDetails.hangoutLink}
                    </a>
                  </div>
                </div>
              ) : !meetingDetails.isOnline && meetingDetails.location ? (
                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-card-foreground">Location</p>
                    <p className="text-sm text-gray-600">{meetingDetails.location}</p>
                  </div>
                </div>
              ) : null}

              {meetingDetails.attendees && meetingDetails.attendees.length > 0 && (
                <div className="flex items-start space-x-3">
                  <Users className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-card-foreground">Attendees</p>
                    <div className="text-sm text-gray-600 space-y-1">
                      {meetingDetails.attendees.map((attendee, index) => (
                        <p key={index}>{attendee.name || attendee.email}</p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {meetingDetails.htmlLink && (
                <div className="flex items-start space-x-3">
                  <Calendar className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-card-foreground">View in Calendar</p>
                    <a
                      href={meetingDetails.htmlLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      Open in Google Calendar
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fallback to booking data if API details not available */}
      {!meetingDetails && bookingData.selectedSlot && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 space-y-4">
          <h4 className="font-semibold text-lg text-card-foreground mb-4">Meeting Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="font-medium text-card-foreground mb-2">Date & Time</p>
              <p className="text-sm text-gray-600">
                {formatInTimeZone(
                  parseISO(bookingData.selectedSlot.start),
                  timeZone,
                  'EEEE, MMMM d, yyyy'
                )}
                <br />
                {formatInTimeZone(parseISO(bookingData.selectedSlot.start), timeZone, 'h:mm a')} -{' '}
                {formatInTimeZone(parseISO(bookingData.selectedSlot.end), timeZone, 'h:mm a')}
                <span className="text-gray-500">
                  {' '}
                  ({formatTimeZoneDisplay(timeZone, parseISO(bookingData.selectedSlot.start))})
                </span>
              </p>
            </div>
            <div>
              <p className="font-medium text-card-foreground mb-2">Meeting Type</p>
              <p className="text-sm text-gray-600">
                {bookingData.isOnline
                  ? 'Online Meeting (Google Meet)'
                  : `In-Person: ${bookingData.location || 'Location TBD'}`}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-center space-x-3">
        <Button
          variant="outline"
          onClick={handleDownloadIcs}
          className="w-full sm:w-auto"
          disabled={!canDownloadIcs}
        >
          <Download className="h-4 w-4" />
          Add to Calendar (.ics)
        </Button>
        <Button onClick={onBookAnother} className="w-full sm:w-auto">
          Book Another Meeting
        </Button>
      </div>
    </div>
  );
}
